import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import {
  ArrowUUpLeftIcon,
  CaretDownIcon,
  RobotIcon,
} from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import { Separator } from 'src/components/ui/separator';
import { cn } from 'src/lib/utils';
import { useListActionConsumers } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';

interface ActionConsumersDrawerProps {
  /** Lineage id of the action workflow this `action_input` node belongs to. */
  workflowId: string;
  /** Shared icon sizing from the host step node. */
  iconClassName: string;
  badgeIconClassName: string;
}

const linkClassName =
  'block rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] transition-colors hover:border-blue-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900/40 dark:hover:border-blue-600 dark:hover:bg-gray-800';

/**
 * Linkback drawer for an `action_input` node: everything that calls this action
 * — every workflow_action step across all versions on both sides, plus the
 * consumers that live outside the workflow engine (AR collections portal-action
 * bindings on a customer profile).
 *
 * The action_input node is where the question "who depends on this?" naturally
 * gets asked — it's the action's public entry point — and the answer has to
 * ignore versions, since consumers pin whatever version they were built
 * against (or track "latest published"). Renders nothing when unused.
 */
export function ActionConsumersDrawer({
  workflowId,
  iconClassName,
  badgeIconClassName,
}: ActionConsumersDrawerProps) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const { data } = useListActionConsumers(workflowId);

  const consumers = data?.consumers ?? [];
  const portalBindings = data?.portalActionBindings ?? [];
  const totalCount = consumers.length + portalBindings.length;
  if (totalCount === 0) return null;

  return (
    <div className="relative overflow-hidden rounded-b-sm">
      <Separator className="px-0" />
      <div className="p-1 bg-accent">
        <button
          type="button"
          className="nodrag nopan w-full cursor-pointer"
          onClick={() => setOpen((v) => !v)}
          title={gt('Workflows and customers that call this action', {
            context: 'tooltip for the action_input consumers drawer',
          })}
        >
          <div className="flex gap-2 items-center">
            <CaretDownIcon
              className={cn(
                iconClassName,
                'transition-transform duration-200',
                open ? '-rotate-90' : '',
              )}
            />
            <Badge variant="outline" className="gap-1">
              <ArrowUUpLeftIcon className={badgeIconClassName} />
              {totalCount}
            </Badge>
            {/* A portal binding is the consumer most likely to be forgotten —
                it lives in another module — so flag it on the collapsed row. */}
            {portalBindings.length > 0 && (
              <Badge variant="outline" className="gap-1">
                <RobotIcon className={badgeIconClassName} />
                {portalBindings.length}
              </Badge>
            )}
          </div>
        </button>

        <div
          className={cn(
            'overflow-hidden transition-all',
            open ? 'max-h-72 mt-2' : 'max-h-0',
          )}
        >
          <div className="nodrag nopan max-h-64 space-y-3 overflow-y-auto border-t border-dashed border-gray-300 pt-2 dark:border-gray-600">
            {consumers.length > 0 && (
              <div className="space-y-1">
                <SectionHeading
                  icon={<ArrowUUpLeftIcon className={badgeIconClassName} />}
                  label={
                    <T context="badge label for the action_input consumers section">
                      Used by
                    </T>
                  }
                />
                {consumers.map((consumer) => (
                  <Link
                    key={consumer.stepId}
                    to="/workflows/$workflowId/v/$versionNumber"
                    params={{
                      workflowId: consumer.workflowId,
                      versionNumber: String(consumer.version),
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClassName}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium">
                        {consumer.workflowName}
                      </span>
                      <span className="shrink-0 opacity-60">
                        {consumer.isPinnedVersion
                          ? gt('v{version} · published', {
                              context: 'consumer version label, pinned version',
                              version: consumer.version,
                            })
                          : gt('v{version}', {
                              context: 'consumer version label',
                              version: consumer.version,
                            })}
                      </span>
                    </div>
                    <div className="truncate opacity-60">
                      {consumer.calledVersion === null
                        ? consumer.stepName
                        : gt('{step} → v{called}', {
                            context:
                              'consumer step name and the action version it calls',
                            step: consumer.stepName,
                            called: consumer.calledVersion,
                          })}
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {portalBindings.length > 0 && (
              <div className="space-y-1">
                <SectionHeading
                  icon={<RobotIcon className={badgeIconClassName} />}
                  label={
                    <T context="badge label for the portal-action bindings section">
                      Portal actions
                    </T>
                  }
                />
                {portalBindings.map((binding) => (
                  <Link
                    key={`${binding.customerId}:${binding.bindingId}`}
                    to="/accounts-receivable/collections/$customerId"
                    params={{ customerId: binding.customerId }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClassName}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium">
                        {binding.customerName}
                      </span>
                      {binding.version !== null && (
                        <span className="shrink-0 opacity-60">
                          {gt('v{version}', {
                            context: 'portal binding pinned action version',
                            version: binding.version,
                          })}
                        </span>
                      )}
                    </div>
                    <div className="truncate opacity-60">
                      {binding.runOnThreadCreation
                        ? gt('{label} · auto-runs', {
                            context:
                              'portal binding label, runs without approval',
                            label: binding.label,
                          })
                        : binding.label}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({
  icon,
  label,
}: {
  icon: ReactNode;
  label: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <Badge variant="outline" className="gap-1">
        {icon}
        {label}
      </Badge>
      <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
    </div>
  );
}
